import DOMPurify from 'dompurify';

// Email-specific sanitization configuration
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  '*': new Set([
    'class',
    'style',
    'id',
    'role',
    'align',
    'valign',
    'width',
    'height',
    'cellpadding',
    'cellspacing',
    'colspan',
    'rowspan',
  ]),
  a: new Set(['href', 'target', 'rel', 'id']),
  img: new Set(['src', 'alt', 'width', 'height']),
  meta: new Set(['charset', 'name', 'content', 'viewport']),
  table: new Set([
    'role',
    'cellpadding',
    'cellspacing',
    'width',
    'style',
    'class',
    'border',
    'bgcolor',
  ]),
  td: new Set([
    'align',
    'valign',
    'width',
    'height',
    'colspan',
    'rowspan',
    'style',
    'class',
    'bgcolor',
  ]),
  tr: new Set(['style', 'class']),
};

const buildEmailSanitizeConfig = () => {
  const allowedTags = new Set<string>([
    'b',
    'i',
    'em',
    'strong',
    'u',
    'br',
    'p',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'td',
    'th',
    'a',
    'img',
    'meta',
    'head',
    'body',
    'html',
    'blockquote',
    'hr',
  ]);

  const allowedAttrs: string[] = [];

  if (ALLOWED_ATTRIBUTES['*']) {
    ALLOWED_ATTRIBUTES['*'].forEach((attr) => {
      if (!allowedAttrs.includes(attr)) {
        allowedAttrs.push(attr);
      }
    });
  }

  Object.keys(ALLOWED_ATTRIBUTES).forEach((tag) => {
    if (tag !== '*') {
      if (ALLOWED_ATTRIBUTES['*']) {
        ALLOWED_ATTRIBUTES['*'].forEach((attr) => {
          if (!allowedAttrs.includes(attr)) {
            allowedAttrs.push(attr);
          }
        });
      }
      ALLOWED_ATTRIBUTES[tag].forEach((attr) => {
        if (!allowedAttrs.includes(attr)) {
          allowedAttrs.push(attr);
        }
      });
    }
  });

  return {
    ALLOWED_TAGS: Array.from(allowedTags),
    ALLOWED_ATTR: allowedAttrs,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^+.:a-z-]|$))/i,
  };
};

const emailSanitizeConfig = buildEmailSanitizeConfig();

/**
 * Sanitizes HTML specifically for email rendering.
 * Uses the ALLOWED_ATTRIBUTES configuration for email-safe attributes.
 */
export const sanitizeEmailHTML = (html: string, options?: { allowAnchors?: boolean }): string => {
  if (typeof document === 'undefined') {
    return html;
  }

  let bodyContent = html;
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    bodyContent = bodyMatch[1];
  }

  let sanitized = DOMPurify.sanitize(bodyContent, emailSanitizeConfig) as unknown as string;

  try {
    const wrapper = document.createElement('div');
    const tempSanitized = sanitized.replace(
      /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
      (_match, before, src, after) => `<img${before} data-temp-src="${src}"${after}>`,
    );
    wrapper.innerHTML = tempSanitized;

    const allElements = wrapper.querySelectorAll('*');
    allElements.forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      const allowedAttrsForTag = new Set<string>();

      if (ALLOWED_ATTRIBUTES['*']) {
        ALLOWED_ATTRIBUTES['*'].forEach((attr) => allowedAttrsForTag.add(attr.toLowerCase()));
      }
      if (ALLOWED_ATTRIBUTES[tagName]) {
        ALLOWED_ATTRIBUTES[tagName].forEach((attr) => allowedAttrsForTag.add(attr.toLowerCase()));
      }

      Array.from(element.attributes).forEach((attr) => {
        if (!allowedAttrsForTag.has(attr.name.toLowerCase())) {
          element.removeAttribute(attr.name);
        }
      });

      if (tagName === 'img') {
        const tempSrc = element.getAttribute('data-temp-src');
        if (tempSrc) {
          element.removeAttribute('data-temp-src');
          element.setAttribute('src', tempSrc);
        }
        const srcAttr = element.getAttribute('src') || '';
        const allowed =
          (srcAttr.startsWith('/assets/') ||
            srcAttr.startsWith('/api/images/') ||
            srcAttr.startsWith('data:') ||
            srcAttr.startsWith('cid:')) &&
          !srcAttr.includes('..');
        if (!allowed) {
          element.remove();
        }
      }

      if (tagName === 'a') {
        const hrefAttr = element.getAttribute('href') || '';
        if (!hrefAttr) {
          return;
        }

        if (options?.allowAnchors) {
          const dangerousProtocols = [
            'javascript:',
            'vbscript:',
            'data:',
            'file:',
            'onclick',
            'onerror',
            'onload',
          ];
          const lowerHref = hrefAttr.toLowerCase().trim();
          const isDangerous = dangerousProtocols.some((protocol) => lowerHref.startsWith(protocol));
          const hasScriptInjection = /<script|javascript:|on\w+\s*=/i.test(hrefAttr);

          if (isDangerous || hasScriptInjection) {
            element.removeAttribute('href');
          }
          return;
        }

        if (!/^(https?|mailto|tel|#|\/)/i.test(hrefAttr) && !hrefAttr.startsWith('javascript:')) {
          element.removeAttribute('href');
        }
      }
    });

    sanitized = wrapper.innerHTML;
  } catch (e) {
    console.warn('Error post-processing email HTML:', e);
  }

  return sanitized;
};
